import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Save, Inbox, Image, Star, MessageSquareQuote, Settings as Cog, Loader2, ExternalLink, UploadCloud } from "lucide-react";
import {
  getToken, setToken, clearToken, adminLogin, adminMe,
  getAdminContacts, getContactStats, updateContact, deleteContact,
  getExamples, saveExample, deleteExample,
  getReferences, saveReference, deleteReference,
  getReviews, saveReview, deleteReview,
  getSettings, saveSettings, uploadImage,
} from "@/lib/api";

const STATUS = [
  { key: "neu", label: "Neu" },
  { key: "in_bearbeitung", label: "In Bearbeitung" },
  { key: "erledigt", label: "Erledigt" },
];
const statusLabel = (k) => STATUS.find((s) => s.key === k)?.label || "Neu";

const field = "w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-neutral-600 focus:border-[#FF3B00] focus:outline-none transition-colors";
const btnPrimary = "inline-flex items-center gap-2 bg-[#FF3B00] text-white font-tech text-xs uppercase tracking-[0.12em] px-4 py-2.5 hover:bg-[#FF5C2B] active:scale-95 transition-all";
const btnGhost = "inline-flex items-center gap-2 border border-white/15 text-neutral-300 font-tech text-xs uppercase tracking-[0.12em] px-4 py-2.5 hover:border-[#FF3B00] hover:text-white transition-colors";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return e?.message || "Fehler";
};

/* ---------------- Image field (URL + upload) ---------------- */
function ImageField({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadImage(f);
      onChange(url);
      toast.success("Bild hochgeladen");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex gap-2">
      <input className={field} placeholder="Bild-URL oder Datei hochladen →" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      <label className={`${btnGhost} cursor-pointer whitespace-nowrap`} data-testid={testid}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />} Upload
        <input type="file" accept="image/*" className="hidden" onChange={onFile} />
      </label>
    </div>
  );
}

/* ---------------- Login ---------------- */
function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { access_token } = await adminLogin(email, password);
      setToken(access_token);
      onSuccess();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 font-body">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login">
        <div className="font-display text-3xl text-white mb-1">SBD<span className="text-[#FF3B00]">.</span> Admin</div>
        <p className="text-neutral-500 text-sm mb-8">Bitte melde dich an, um Inhalte zu verwalten.</p>
        <label className="font-tech text-xs uppercase tracking-[0.15em] text-neutral-500">E-Mail</label>
        <input className={`${field} mt-2 mb-4`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
        <label className="font-tech text-xs uppercase tracking-[0.15em] text-neutral-500">Passwort</label>
        <input className={`${field} mt-2 mb-6`} type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
        <button type="submit" disabled={loading} className={`${btnPrimary} w-full justify-center py-3.5`} data-testid="admin-login-submit">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Anmelden"}
        </button>
      </form>
    </div>
  );
}

/* ---------------- Anfragen ---------------- */
function AnfragenTab() {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, neu: 0, in_bearbeitung: 0, erledigt: 0 });
  const [name, setName] = useState("");
  const [status, setStatus] = useState("alle");

  const load = useCallback(async () => {
    try {
      const params = {};
      if (name) params.name = name;
      if (status !== "alle") params.status = status;
      const [c, s] = await Promise.all([getAdminContacts(params), getContactStats()]);
      setContacts(c);
      setStats(s);
    } catch (e) { toast.error(errMsg(e)); }
  }, [name, status]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, newStatus) => {
    try { await updateContact(id, { status: newStatus }); toast.success("Status aktualisiert"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Anfrage wirklich löschen?")) return;
    try { await deleteContact(id); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="tab-anfragen">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-8">
        {[["Gesamt", stats.total], ["Neu", stats.neu], ["In Bearbeitung", stats.in_bearbeitung], ["Erledigt", stats.erledigt]].map(([l, v]) => (
          <div key={l} className="bg-[#0A0A0A] p-5">
            <div className="font-display text-3xl text-white">{v}</div>
            <div className="font-tech text-[11px] uppercase tracking-[0.14em] text-neutral-500">{l}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <input className={`${field} max-w-xs`} placeholder="Nach Name filtern…" value={name} onChange={(e) => setName(e.target.value)} data-testid="filter-name" />
        <select className={`${field} max-w-xs`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
          <option value="alle">Alle Status</option>
          {STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>

      {contacts.length === 0 && <p className="text-neutral-500 text-sm">Keine Anfragen gefunden.</p>}
      <div className="space-y-3">
        {contacts.map((c) => (
          <div key={c.id} className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`contact-row-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-xl text-white">{c.name}</span>
                  <span className={`font-tech text-[10px] uppercase tracking-[0.12em] px-2 py-1 ${c.status === "erledigt" ? "bg-green-600/20 text-green-400" : c.status === "in_bearbeitung" ? "bg-yellow-600/20 text-yellow-400" : "bg-[#FF3B00]/20 text-[#FF3B00]"}`}>
                    {statusLabel(c.status)}
                  </span>
                </div>
                <div className="text-neutral-400 text-sm mt-1">
                  {c.email}{c.phone ? ` · ${c.phone}` : ""}{c.company ? ` · ${c.company}` : ""}
                </div>
                {(c.paket || c.has_website) && (
                  <div className="text-neutral-500 text-xs mt-1">
                    {c.paket ? `Paket: ${c.paket}` : ""}{c.paket && c.has_website ? " · " : ""}{c.has_website ? `Bereits Website: ${c.has_website}` : ""}
                  </div>
                )}
                {c.budget && <div className="text-neutral-500 text-xs mt-1">Start: {c.budget}</div>}
                <p className="text-neutral-300 text-sm mt-3 whitespace-pre-wrap max-w-2xl">{c.message}</p>
                <div className="text-neutral-600 text-xs mt-2">{new Date(c.created_at).toLocaleString("de-DE")}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select className={`${field} py-2`} value={c.status} onChange={(e) => changeStatus(c.id, e.target.value)} data-testid={`contact-status-${c.id}`}>
                  {STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <button onClick={() => remove(c.id)} className="p-2.5 border border-white/15 text-neutral-400 hover:text-red-400 hover:border-red-400/50 transition-colors" data-testid={`contact-delete-${c.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Beispiele ---------------- */
function BeispieleTab() {
  const [items, setItems] = useState([]);
  const load = () => getExamples().then(setItems).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const edit = (i, key, val) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const save = async (it) => {
    try { await saveExample({ ...it, points: typeof it.points === "string" ? it.points.split("\n").filter(Boolean) : it.points }); toast.success("Gespeichert"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; try { await deleteExample(id); toast.success("Gelöscht"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const add = () => setItems((arr) => [...arr, { title: "Neues Beispiel", tag: "Beispiel", image: "", points: [], order: arr.length }]);

  return (
    <div data-testid="tab-beispiele" className="space-y-5">
      <button onClick={add} className={btnGhost} data-testid="example-add"><Plus size={15} /> Beispiel hinzufügen</button>
      {items.map((it, i) => (
        <div key={it.id || i} className="border border-white/10 bg-[#0A0A0A] p-5 grid md:grid-cols-[160px_1fr] gap-5" data-testid={`example-item-${i}`}>
          <div className="border border-white/10 bg-[#0F0F0F] aspect-video overflow-hidden">
            {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover object-top" /> : <div className="w-full h-full grid place-items-center text-neutral-600"><Image size={24} /></div>}
          </div>
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={field} placeholder="Titel" value={it.title || ""} onChange={(e) => edit(i, "title", e.target.value)} />
              <input className={field} placeholder="Tag (z.B. Beispiel · Dienstleister)" value={it.tag || ""} onChange={(e) => edit(i, "tag", e.target.value)} />
            </div>
            <ImageField value={it.image} onChange={(url) => edit(i, "image", url)} testid={`example-upload-${i}`} />
            <textarea className={`${field} resize-none`} rows={3} placeholder="Punkte – ein Punkt pro Zeile" value={Array.isArray(it.points) ? it.points.join("\n") : it.points} onChange={(e) => edit(i, "points", e.target.value)} />
            <div className="flex gap-2">
              <button onClick={() => save(it)} className={btnPrimary} data-testid={`example-save-${i}`}><Save size={14} /> Speichern</button>
              {it.id && <button onClick={() => remove(it.id)} className="p-2.5 border border-white/15 text-neutral-400 hover:text-red-400 hover:border-red-400/50 transition-colors"><Trash2 size={16} /></button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Referenzen ---------------- */
function ReferenzenTab() {
  const [items, setItems] = useState([]);
  const load = () => getReferences().then(setItems).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const edit = (i, key, val) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const save = async (it) => { try { await saveReference(it); toast.success("Gespeichert"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; try { await deleteReference(id); toast.success("Gelöscht"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const add = () => setItems((arr) => [...arr, { title: "Neues Projekt", cat: "Landingpage", year: "2026", href: "#kontakt", image: "", order: arr.length }]);

  return (
    <div data-testid="tab-referenzen" className="space-y-5">
      <button onClick={add} className={btnGhost} data-testid="reference-add"><Plus size={15} /> Referenz hinzufügen</button>
      {items.map((it, i) => (
        <div key={it.id || i} className="border border-white/10 bg-[#0A0A0A] p-5 grid md:grid-cols-[160px_1fr] gap-5" data-testid={`reference-item-${i}`}>
          <div className="border border-white/10 bg-[#0F0F0F] aspect-video overflow-hidden">
            {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover object-top" /> : <div className="w-full h-full grid place-items-center text-neutral-600"><Image size={24} /></div>}
          </div>
          <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <input className={field} placeholder="Titel" value={it.title || ""} onChange={(e) => edit(i, "title", e.target.value)} />
              <input className={field} placeholder="Kategorie" value={it.cat || ""} onChange={(e) => edit(i, "cat", e.target.value)} />
              <input className={field} placeholder="Jahr" value={it.year || ""} onChange={(e) => edit(i, "year", e.target.value)} />
            </div>
            <input className={field} placeholder="Link (https:// oder #kontakt)" value={it.href || ""} onChange={(e) => edit(i, "href", e.target.value)} />
            <input className={field} placeholder="Ergebnis-Badge (z.B. +40% Anfragen, Live-Projekt) – optional" value={it.result || ""} onChange={(e) => edit(i, "result", e.target.value)} data-testid={`reference-result-${i}`} />
            <ImageField value={it.image} onChange={(url) => edit(i, "image", url)} testid={`reference-upload-${i}`} />
            <div className="flex gap-2">
              <button onClick={() => save(it)} className={btnPrimary} data-testid={`reference-save-${i}`}><Save size={14} /> Speichern</button>
              {it.id && <button onClick={() => remove(it.id)} className="p-2.5 border border-white/15 text-neutral-400 hover:text-red-400 hover:border-red-400/50 transition-colors"><Trash2 size={16} /></button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Einstellungen ---------------- */
function EinstellungenTab() {
  const [s, setS] = useState(null);
  useEffect(() => { getSettings().then(setS).catch((e) => toast.error(errMsg(e))); }, []);
  if (!s) return <Loader2 className="animate-spin text-neutral-500" />;

  const upd = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const save = async () => {
    try {
      await saveSettings({ ...s, price_features: typeof s.price_features === "string" ? s.price_features.split("\n").filter(Boolean) : s.price_features });
      toast.success("Einstellungen gespeichert");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="tab-einstellungen" className="max-w-2xl space-y-5">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="font-tech text-xs uppercase tracking-[0.14em] text-neutral-500">Preis</label><input className={`${field} mt-2`} value={s.price || ""} onChange={(e) => upd("price", e.target.value)} data-testid="settings-price" /></div>
        <div><label className="font-tech text-xs uppercase tracking-[0.14em] text-neutral-500">Preis-Zusatz</label><input className={`${field} mt-2`} value={s.price_note || ""} onChange={(e) => upd("price_note", e.target.value)} /></div>
      </div>
      <div>
        <label className="font-tech text-xs uppercase tracking-[0.14em] text-neutral-500">Enthaltene Leistungen (eine pro Zeile)</label>
        <textarea className={`${field} mt-2 resize-none`} rows={8} value={Array.isArray(s.price_features) ? s.price_features.join("\n") : s.price_features} onChange={(e) => upd("price_features", e.target.value)} data-testid="settings-features" />
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><label className="font-tech text-xs uppercase tracking-[0.14em] text-neutral-500">E-Mail</label><input className={`${field} mt-2`} value={s.contact_email || ""} onChange={(e) => upd("contact_email", e.target.value)} data-testid="settings-email" /></div>
        <div><label className="font-tech text-xs uppercase tracking-[0.14em] text-neutral-500">Telefon</label><input className={`${field} mt-2`} value={s.contact_phone || ""} onChange={(e) => upd("contact_phone", e.target.value)} /></div>
        <div><label className="font-tech text-xs uppercase tracking-[0.14em] text-neutral-500">Instagram</label><input className={`${field} mt-2`} value={s.contact_instagram || ""} onChange={(e) => upd("contact_instagram", e.target.value)} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="font-tech text-xs uppercase tracking-[0.14em] text-neutral-500">WhatsApp-Nummer</label><input className={`${field} mt-2`} value={s.whatsapp || ""} onChange={(e) => upd("whatsapp", e.target.value)} data-testid="settings-whatsapp" /></div>
        <div><label className="font-tech text-xs uppercase tracking-[0.14em] text-neutral-500">WhatsApp-Nachricht (vorausgefüllt)</label><input className={`${field} mt-2`} value={s.whatsapp_message || ""} onChange={(e) => upd("whatsapp_message", e.target.value)} data-testid="settings-whatsapp-msg" /></div>
      </div>
      <button onClick={save} className={btnPrimary} data-testid="settings-save"><Save size={14} /> Speichern</button>
    </div>
  );
}

/* ---------------- Bewertungen ---------------- */
function BewertungenTab() {
  const [items, setItems] = useState([]);
  const load = () => getReviews().then(setItems).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const edit = (i, key, val) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const save = async (it) => { try { await saveReview({ ...it, rating: Number(it.rating) || 5 }); toast.success("Gespeichert"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; try { await deleteReview(id); toast.success("Gelöscht"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const add = () => setItems((arr) => [...arr, { name: "Neuer Kunde", role: "", rating: 5, text: "", order: arr.length }]);

  return (
    <div data-testid="tab-bewertungen" className="space-y-5">
      <button onClick={add} className={btnGhost} data-testid="review-add"><Plus size={15} /> Bewertung hinzufügen</button>
      {items.map((it, i) => (
        <div key={it.id || i} className="border border-white/10 bg-[#0A0A0A] p-5 space-y-3" data-testid={`review-item-${i}`}>
          <div className="grid sm:grid-cols-[1fr_1fr_120px] gap-3">
            <input className={field} placeholder="Name" value={it.name || ""} onChange={(e) => edit(i, "name", e.target.value)} />
            <input className={field} placeholder="Rolle / Firma" value={it.role || ""} onChange={(e) => edit(i, "role", e.target.value)} />
            <select className={field} value={it.rating || 5} onChange={(e) => edit(i, "rating", e.target.value)}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} Sterne</option>)}
            </select>
          </div>
          <textarea className={`${field} resize-none`} rows={3} placeholder="Bewertungstext" value={it.text || ""} onChange={(e) => edit(i, "text", e.target.value)} />
          <div className="flex gap-2">
            <button onClick={() => save(it)} className={btnPrimary} data-testid={`review-save-${i}`}><Save size={14} /> Speichern</button>
            {it.id && <button onClick={() => remove(it.id)} className="p-2.5 border border-white/15 text-neutral-400 hover:text-red-400 hover:border-red-400/50 transition-colors"><Trash2 size={16} /></button>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Shell ---------------- */
const TABS = [
  { key: "anfragen", label: "Anfragen", icon: Inbox },
  { key: "beispiele", label: "Beispiele", icon: Image },
  { key: "referenzen", label: "Referenzen", icon: Star },
  { key: "bewertungen", label: "Bewertungen", icon: MessageSquareQuote },
  { key: "einstellungen", label: "Einstellungen", icon: Cog },
];

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("anfragen");

  const check = useCallback(() => {
    if (!getToken()) { setAuthed(false); return; }
    adminMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);
  useEffect(() => { check(); }, [check]);

  const logout = () => { clearToken(); setAuthed(false); toast.success("Abgemeldet"); };

  if (authed === null) return <div className="min-h-screen bg-[#050505] grid place-items-center"><Loader2 className="animate-spin text-neutral-500" /></div>;
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F4F4F0] font-body" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-[#050505]/90 backdrop-blur-xl z-10">
        <div className="mx-auto max-w-[1200px] px-6 h-16 flex items-center justify-between">
          <div className="font-display text-xl text-white">SBD<span className="text-[#FF3B00]">.</span> Admin</div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="font-tech text-xs uppercase tracking-[0.12em] text-neutral-400 hover:text-white flex items-center gap-1"><ExternalLink size={13} /> Seite ansehen</a>
            <button onClick={logout} className={btnGhost} data-testid="admin-logout"><LogOut size={14} /> Abmelden</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 py-8 grid md:grid-cols-[200px_1fr] gap-8">
        <nav className="flex md:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2.5 px-4 py-3 font-tech text-xs uppercase tracking-[0.12em] transition-colors shrink-0 ${tab === t.key ? "bg-[#FF3B00] text-white" : "text-neutral-400 hover:text-white hover:bg-white/5"}`}
              data-testid={`admin-tab-${t.key}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </nav>
        <div>
          {tab === "anfragen" && <AnfragenTab />}
          {tab === "beispiele" && <BeispieleTab />}
          {tab === "referenzen" && <ReferenzenTab />}
          {tab === "bewertungen" && <BewertungenTab />}
          {tab === "einstellungen" && <EinstellungenTab />}
        </div>
      </div>
    </div>
  );
}
